import { useCallback, useState } from 'react';
import axios from 'axios';
import { ColumnsType } from 'antd/es/table';
import Data from '../Data/Data.json';
import { Crypter } from '../Functions/Security/Crypter';
import { useAuthHeader } from '../Services/Auth/sessionAuth';
import Echeance from '../Models/Echeance';
import {
    EcheancierPretResult,
    EcheancierTotaux,
    EcheancierVersion,
    EcheancierVersionsResult,
    EcheancierVersionLigne,
    EcheancierVersionTotaux,
    normalizeEcheancierResult,
    normalizeEcheancierVersions
} from '../Models/EcheancierPret';
import formatNumber from '../Functions/Traitement/FormatNumber';
import formatOrderDate from '../Functions/Traitement/formatOrderDate';

const renderMontant = (value: any) => (typeof value === 'number' ? formatNumber(value) : value ?? '');
const renderDate = (value: any) => (value ? formatOrderDate(String(value).substr(0, 10), 'JJ-MM-AAAA') : '');

/** Colonnes d'affichage de l'échéancier (formatage uniquement au rendu). */
export const echeancierColumns: ColumnsType<Echeance> = [
    { title: 'N° Séq', dataIndex: 'numSeq', width: '100px' },
    { title: 'Date Echéance', dataIndex: 'dateEcheacnce', width: '100px', render: renderDate },
    { title: 'Capital (C)', dataIndex: 'capital', width: '100px', render: renderMontant },
    { title: 'Amortiss (A)', dataIndex: 'amortissement', width: '100px', render: renderMontant },
    { title: 'Inttérets (I)', dataIndex: 'interet', width: '100px', render: renderMontant },
    { title: 'Taxes (T)', dataIndex: 'taxe', width: '100px', render: renderMontant },
    { title: 'Epargne (E)', dataIndex: 'epargne', width: '100px', render: renderMontant },
    { title: 'AHT (A+I) ', dataIndex: 'anHT', width: '100px', render: renderMontant },
    { title: 'ATTC (AHT+I) ', dataIndex: 'anTTC', width: '100px', render: renderMontant }
];

/** Mappe les totaux backend vers les champs de totaux des formulaires existants. */
export function totauxFormFields(totaux: EcheancierTotaux | null) {
    if (!totaux) {
        return { TotAm: '', TotIt: '', TotTaxe: '', TotAnnuite: '', TotAnTTC: '', TotEpargn: '' };
    }
    return {
        TotAm: formatNumber(totaux.totAmortissement),
        TotIt: formatNumber(totaux.totInteret),
        TotTaxe: formatNumber(totaux.totTaxe),
        TotAnnuite: formatNumber(totaux.totAnnuiteHT),
        TotAnTTC: formatNumber(totaux.totAnnuiteTTC),
        TotEpargn: formatNumber(totaux.totEpargne)
    };
}

interface UseEcheancierPretReturn {
    echeances: Echeance[];
    totaux: EcheancierTotaux | null;
    /** Version affichee et derniere version generee (depuis le dernier charger). */
    version: number;
    versionCourante: number;
    isCourant: boolean;
    /** Liste des versions disponibles (depuis le dernier chargerVersions). */
    versions: EcheancierVersion[];
    loading: boolean;
    error: string | null;
    charger: (numDos: string, typeEcp?: 'PRET' | 'SIMULATION') => Promise<EcheancierPretResult | null>;
    /** Charge un echeancier deja calcule (apercu non persiste) sans appel serveur. */
    chargerLocal: (result: EcheancierPretResult) => void;
    chargerVersions: (numDos: string, typeEcp?: 'PRET' | 'SIMULATION') => Promise<EcheancierVersionsResult | null>;
    /** Bascule l'affichage sur une version deja chargee (aucun appel serveur).
     *  Retourne les lignes/totaux de la version pour les ecrans qui gardent
     *  leur propre etat (Simulation). */
    afficherVersion: (version: number) => EcheancierPretResult | null;
    clear: () => void;
}

/**
 * Consultation de l'échéancier d'un prêt ou d'une simulation.
 * Le tableau est généré en base par les procédures stockées de chaque étape
 * (simulation, demande, proposition, validation, modification) : le frontend
 * ne déclenche jamais le calcul, il ne fait que lire le résultat persisté.
 * typeEcp : 'PRET' (table ECHEANCE_PRET) ou 'SIMULATION' (ECHEANCE_PRET_SIMULATION).
 */
const useEcheancierPret = (): UseEcheancierPretReturn => {
    const authHeader = useAuthHeader();
    const [echeances, setEcheances] = useState<Echeance[]>([]);
    const [totaux, setTotaux] = useState<EcheancierTotaux | null>(null);
    const [version, setVersion] = useState(0);
    const [versionCourante, setVersionCourante] = useState(0);
    const [isCourant, setIsCourant] = useState(true);
    const [versions, setVersions] = useState<EcheancierVersion[]>([]);
    const [lignesVersions, setLignesVersions] = useState<EcheancierVersionLigne[]>([]);
    const [totauxVersions, setTotauxVersions] = useState<EcheancierVersionTotaux[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const charger = useCallback(async (numDos: string, typeEcp: 'PRET' | 'SIMULATION' = 'PRET'): Promise<EcheancierPretResult | null> => {
        if (!numDos) return null;

        setLoading(true);
        setError(null);
        try {
            const response = await axios.request({
                method: 'get',
                maxBodyLength: Infinity,
                url: Data.ApiUrl.GetEcheancierPret,
                headers: {
                    'CypherNumDos': Crypter.encrypt(numDos),
                    'CypherTypeEcp': Crypter.encrypt(typeEcp),
                    'Authorization': authHeader(),
                    'Content-Type': 'application/json'
                }
            });

            const res = JSON.parse(Crypter.decrypt(response.data)).Value;
            if (res.StatutCode === 200 && res.Result) {
                const result = normalizeEcheancierResult(res.Result);
                setEcheances(result.echeances);
                setTotaux(result.totaux);
                setVersion(result.version);
                setVersionCourante(result.versionCourante);
                setIsCourant(result.isCourant);
                // Rafraichit la liste des versions (marqueur isCourant)
                chargerVersions(numDos, typeEcp);
                return result;
            }
            setError(res.Message || "Aucun échéancier disponible pour ce dossier");
            return null;
        } catch (e: any) {
            setError(String(e));
            return null;
        } finally {
            setLoading(false);
        }
    }, [authHeader]);

    // Charge un echeancier recu/calcule sans appel serveur (apercu avant
    // enregistrement : les lignes ne sont pas persistees en base).
    const chargerLocal = useCallback((result: EcheancierPretResult) => {
        setEcheances(result.echeances);
        setTotaux(result.totaux);
        setVersion(result.version);
        setVersionCourante(result.versionCourante);
        setIsCourant(result.isCourant);
        setVersions([]);
        setLignesVersions([]);
        setTotauxVersions([]);
        setError(null);
    }, []);

    const chargerVersions = useCallback(async (numDos: string, typeEcp: 'PRET' | 'SIMULATION' = 'PRET'): Promise<EcheancierVersionsResult | null> => {
        if (!numDos) return null;

        try {
            const response = await axios.request({
                method: 'get',
                maxBodyLength: Infinity,
                url: Data.ApiUrl.GetEcheancierVersions,
                headers: {
                    'CypherNumDos': Crypter.encrypt(numDos),
                    'CypherTypeEcp': Crypter.encrypt(typeEcp),
                    'Authorization': authHeader(),
                    'Content-Type': 'application/json'
                }
            });

            const res = JSON.parse(Crypter.decrypt(response.data)).Value;
            if (res.StatutCode === 200 && res.Result) {
                const result = normalizeEcheancierVersions(res.Result);
                setVersions(result.versions);
                setLignesVersions(result.lignes);
                setTotauxVersions(result.totaux);
                return result;
            }
            setVersions([]);
            setLignesVersions([]);
            setTotauxVersions([]);
            return null;
        } catch (e: any) {
            setVersions([]);
            setLignesVersions([]);
            setTotauxVersions([]);
            return null;
        }
    }, [authHeader]);

    // Bascule l'affichage sur une version deja chargee (lignes + totaux en memoire).
    const afficherVersion = useCallback((v: number): EcheancierPretResult | null => {
        const lignes = lignesVersions.filter(l => l.version === v);
        if (lignes.length === 0) return null;

        const t = totauxVersions.find(t => t.version === v);
        const totaux: EcheancierTotaux = t ? {
            totAmortissement: t.totAmortissement,
            totInteret: t.totInteret,
            totTaxe: t.totTaxe,
            totEpargne: t.totEpargne,
            totAnnuiteHT: t.totAnnuiteHT,
            totAnnuiteTTC: t.totAnnuiteTTC
        } : {
            totAmortissement: lignes.reduce((s, e) => s + e.amortissement, 0),
            totInteret: lignes.reduce((s, e) => s + e.interet, 0),
            totTaxe: lignes.reduce((s, e) => s + e.taxe, 0),
            totEpargne: lignes.reduce((s, e) => s + e.epargne, 0),
            totAnnuiteHT: lignes.reduce((s, e) => s + e.anHT, 0),
            totAnnuiteTTC: lignes.reduce((s, e) => s + e.anTTC, 0)
        };

        const isCourantV = versions.find(x => x.version === v)?.isCourant === 1;
        setEcheances(lignes);
        setTotaux(totaux);
        setVersion(v);
        setIsCourant(isCourantV);

        return { echeances: lignes, totaux, version: v, versionCourante, isCourant: isCourantV };
    }, [lignesVersions, totauxVersions, versions, versionCourante]);

    const clear = useCallback(() => {
        setEcheances([]);
        setTotaux(null);
        setVersion(0);
        setVersionCourante(0);
        setIsCourant(true);
        setVersions([]);
        setLignesVersions([]);
        setTotauxVersions([]);
        setError(null);
    }, []);

    return { echeances, totaux, version, versionCourante, isCourant, versions, loading, error, charger, chargerLocal, chargerVersions, afficherVersion, clear };
};

export default useEcheancierPret;
